import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { OfferPageActions, OfferPageFilterType, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS } from 'constants/flight'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import TextButton from 'LuxKit/Button/TextButton'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Pill from 'LuxKit/Pill'
import { selectLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferPageBNBLcta from '../OfferPageBNBLcta'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable/Clickable'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import config from 'constants/config'
import Group from 'components/utils/Group'
import { max, unique } from 'lib/array/arrayUtils'
import ModalContext from 'contexts/ModalContext'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import TextLink from 'LuxKit/TextLink'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import AccommodationPackageTilePricingBlock from 'components/AccommodationKit/AccommodationPackageTilePricingBlock'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import Label from 'LuxKit/Label'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { addFlightPrice, feesToObject } from 'lib/offer/pricing'
import FlashHotelMarginText from './FlashHotelMarginText'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchOfferPackageAddons } from 'actions/AddonActions'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { getPriceDisplayFunction, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import ContactContext from 'contexts/contactContext'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import { useCurrentSiteTakeoverV2 } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import Expander from 'components/utils/Expander'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { formatGuestsDisplay } from 'lib/string/stringUtils'

interface Props {
  offer: App.Offer
  roomsBeforeSurcharge?: number
  packageView: App.OfferPackageOptionView
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  rooms: Array<OfferPageRoom>
  checkIn?: string
  fetching?: boolean
  currency: string
  isSpoofed: boolean
  isStoreMode: boolean
  defaultDepositAmountPercentage: number
  isFlightsSelected: boolean
  charterBundleRoute?: App.CharterBundleRoute
  priceMode: OfferPagePriceMode
}

function FlashHotelOptionPriceDetails(props: Props) {
  const {
    offer,
    roomsBeforeSurcharge,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    fetching,
    currency,
    isSpoofed,
    isStoreMode,
    defaultDepositAmountPercentage,
    isFlightsSelected,
    packageView,
    charterBundleRoute,
    priceMode,
  } = props

  const {
    package: pkg,
    flightsAvailable,
    pricing,
    memberPricing,
  } = packageView
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])

  const { totalFlightPrice: flightPrice } = useOfferPageFlightPrice()
  const { loading: charterFlightLoading } = useOfferPageFlightPrice()

  const pricingWithFlights = flightPrice ? addFlightPrice(pricing, flightPrice) : undefined
  const memberPricingWithFlights = (memberPricing && flightPrice) ? addFlightPrice(memberPricing, flightPrice) : undefined

  const fees = feesToObject(pricing.fees)

  const priceWithoutPropertyFees = pricing.price - (fees.property ?? 0)

  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const bundledWithFlightsOnly = offer.bundledWithFlightsOnly || !!charterBundleRoute
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const offerPageDispatch = useOfferPageDispatch()
  const { currentRegionCode } = useContext(GeoContext)

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (flightsAvailable || bundledWithFlightsOnly)
  const hasFlightPrice = !!pricingWithFlights
  const missingCharterFlightPrice = !!charterBundleRoute && !flightPrice
  const shouldShowCharterFlightErrorMessage = missingCharterFlightPrice && !charterFlightLoading
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo && !shouldShowCharterFlightErrorMessage

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const extraGuestSurchargesPayableAtProperty = !!pkg.roomRate?.extraGuestSurcharges.length && pkg.roomRate?.extraGuestSurcharges.some(surcharge => surcharge?.payableAtProperty)

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch, pkg.roomType])

  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlightPrice && bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = !!pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const discountPercent = bundledWithFlightsOnly ? pricingWithFlights?.discountPercent : max([pricing.discountPercent, pricingWithFlights?.discountPercent ?? 0])
  const showDynamicPricingUrgencyTag = !!roomsBeforeSurcharge
  const contact = useContext(ContactContext)
  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
  }, [checkInMoment])

  const showPriceDetails = !!fees.taxesAndFees || !!fees.property || config.agentHub.isEnabled
  const showMemberPrice = !!memberPricing && memberPricing.price > 0

  const { selectedTargetUpgradePackage } = useLoyaltyOfferPageTargetStayUpgrade(pkg)
  const showBNBL = packageView.capacityValid && offer.hasBuyNowBookLater && pkg.allowBuyNowBookLater && config.accommodation.hasBnbl
  const canAddToUserCart = canBuyNow

  const shouldShowDiscount = !offer.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && (bundledWithFlightsOnly ? hasFlightPrice : true)
  const shouldShowValue = pricing.value > pricing.price && pkg.shouldDisplayValue
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currency, checkInMoment, pricing.price)
  const isIndiaRegion = currentRegionCode === 'IN'
  const isFlexibleCancellation = isFlexibleCancellationPolicy(packageView.package?.roomRate?.cancellationPolicy)

  const roomRateIsReservableForZero = isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const showReserveForZero = (isReserveForZeroEnabled || isSpoofed) && roomRateIsReservableForZero
  const showDepositTag = isDepositEnabled && !isSpoofed
  const showFlexiblePaymentTag = isDepositEnabled && isSpoofed

  const showFlightsDealTag = flightsEnabled && flightOrigin && flightDestination && deals.length > 0

  const isMobileDevice = useIsMobileDevice()
  const showHotelAndFlightsToggle = !isMobileDevice && flightsEnabled && !bundledWithFlightsOnly
  const hideHotelPriceBlock = showHotelAndFlightsToggle && isFlightsSelected || missingCharterFlightPrice
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected || missingCharterFlightPrice
  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture
  const showModal = useContext(ModalContext)
  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      const currentPrice = isFlightsSelected ? pricingWithFlights?.price : pricing.price

      if (currentPrice) {
        Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomRate?.id, includesFlights: isFlightsSelected, packageOptionPrice: currentPrice }))
      }
    } else {
      trackEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, isFlightsSelected, offer, pkg.roomRate?.id, pricing.price, pricingWithFlights?.price, trackEvent])

  const onFlexPaymentPolicyOpen = useCallback(async() => {
    showModal(
      <ReserveForZeroAndDepositPolicyModal
        depositAmountPercentage={depositAmountPercentage}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />,
    )
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])
  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])

  const otherFees = {
    propertyFees: packageView.propertyFees,
    extraGuestsSurcharge: extraGuestSurchargesPayableAtProperty ? packageView.extraGuestsSurcharge : 0,
  }

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isSpoofed) {
      dispatch(fetchOfferPackageAddons(offer.id, pkg.id))
    }
  }, [dispatch, offer.id, pkg.id, isSpoofed])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      flightPrice,
      skip: hideHotelAndFlightsPriceBlock,
    }),
  ], [offer, packageView, hideHotelAndFlightsPriceBlock, currentRegionCode, flightPrice])

  const isLuxPlusMonthTakeoverEnabled = useCurrentSiteTakeoverV2({ offer })?.id === LUX_PLUS_MONTH_TAKEOVER_ID
  const urgencyLabels = useMemo(() => offer.urgencyTags.filter(tag => tag.type !== 'new'), [offer.urgencyTags])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const totalGuests = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const guestsDisplayText = useMemo(() => {
    if (!charterBundleRoute || !totalGuests) return undefined
    return formatGuestsDisplay(totalGuests)
  }, [charterBundleRoute, totalGuests])

  return <>
    <Group direction="horizontal" gap={8} wrap="wrap" noDisplayWhenEmpty>
      {shouldShowDiscount && !showDynamicPricingUrgencyTag && !!discountPercent && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={discountPercent}
      />}
      {showDynamicPricingUrgencyTag && !!roomsBeforeSurcharge &&
        <UrgencyLabel variant="critical2">
          {`${roomsBeforeSurcharge} left at this price`}
        </UrgencyLabel>}
      {showDepositTag && <SecureWithDepositTagAndModal
        isFlexibleCancellation={isFlexibleCancellation}
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
      />}
      {showFlexiblePaymentTag && <Clickable onClick={onFlexPaymentPolicyOpen}>
        <Pill startIcon={<LineClockDollarIcon />} kind="tertiary" variant="primary">
          Flexible payment options
        </Pill>
      </Clickable>}
    </Group>
    <AccommodationPackageTilePricingBlocks>
      {!bundledWithFlightsOnly && !hideHotelPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="pricing"
        title={(flightsEnabled && !showHotelAndFlightsToggle) ? 'Hotel' : undefined}
        callToAction={<Group gap={8} direction="vertical" horizontalAlign="stretch">
          <OfferBookButton
            size="large"
            type="submit"
            data-testid="HotelBookingBtn"
            disabled={fetching}
            packageView={packageView}
            onSubmit={onSubmit}
          >
            {canBuyNow ? 'Book now' : 'Check availability'}
          </OfferBookButton>
          {canAddToUserCart && <Expander expanded={!selectedTargetUpgradePackage}>
            <OfferAddToCartButton
              size="large"
              fit="flex"
              type="submit"
              disabled={fetching}
              packageView={packageView}
            />
          </Expander>}
        </Group>}
      >
        <VerticalSpacer gap={12}>
          <VerticalSpacer gap={4}>
            {isLuxPlusMonthTakeoverEnabled && <OfferLabels offer={offer} saveAmount={showMemberPrice ? pricing.price - memberPricing?.price : undefined} urgencyLabels={urgencyLabels} />}
            <div>
              {!isStoreMode && isSpoofed && <FlashHotelMarginText
                checkIn={checkInMoment?.format(ISO_DATE_FORMAT)}
                pkg={pkg}
                type="exact"
              />}

              <PriceRowPriceCaption>
                {pluralizeToString('night', pkg.duration)} {!checkIn && 'from'}
              </PriceRowPriceCaption>
              <LuxPlusPriceStack
                size={!flightsEnabled || showHotelAndFlightsToggle ? 'L' : 'S'}
                price={convertToDisplay(pricing.price)}
                memberPrice={memberPricing?.price && convertToDisplay(memberPricing?.price)}
                saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
                testid="offer-package-price"
                offerConfig={offer.luxPlus}
              />
              <PriceRowTaxesAndFees />
              {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={pkg.roomPolicyDescription}
              />}
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                discountPercentage={shouldShowDiscount && showDynamicPricingUrgencyTag ? discountPercent : undefined}
                size="M"
                originalValue={convertToDisplay(pricing.value)}
              />}
              {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
            </div>
            <PriceRowAgentHubCommission
              offerId={offer.id}
              size="M"
              productType={offer.productType}
              kind="plain"
              offerTotal={priceWithoutPropertyFees}
              packageId={pkg.id}
            />
            <div>
              {showPriceDetails && <OfferPriceDetailsRow
                trigger="price-row"
                triggerSize="M"
                offerPage
                offer={offer}
                duration={pkg.duration}
                propertyFees={fees.property}
                hotelPrice={priceWithoutPropertyFees}
                otherFees={otherFees}
                rooms={rooms}
                extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
                hotelMemberPrice={packageView.hotelMemberPrice}
                showMemberPrice={showMemberPrice}
                cta={<Group
                  direction="vertical"
                  tabletDirection="horizontal"
                  horizontalAlign="stretch"
                  tabletHorizontalAlign="end"
                  gap={8}
                >
                  {canAddToUserCart && <OfferAddToCartButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                  />}
                  <OfferBookButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                    onSubmit={onSubmit}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </OfferBookButton>
                </Group>}
              />}
              {showReserveForZero && <TextLink
                colour="highlight-secondary"
                size="small"
                weight="bold"
                endIcon={<LineInfoCircleIcon colour="primary" />}
                onClick={openReserveForZeroPolicyModal}
              >
                Reserve now for free
              </TextLink>}
            </div>
          </VerticalSpacer>
          <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || (showHotelAndFlightsToggle && isFlightsSelected)) ? 'estimate' : 'exact'}
            modalTitle={packageView.package.roomType.name}
          />
        </VerticalSpacer>
      </AccommodationPackageTilePricingBlock>}

      {flightsEnabled && !hideHotelAndFlightsPriceBlock && <AccommodationPackageTilePricingBlock
        data-testid="flights-pricing"
        title={(!flightsBundledNoCache && !showHotelAndFlightsToggle) ? 'Hotel + flights' : undefined}
        callToAction={<Group direction="vertical" gap={8}>
          {!hasFlightPrice && flightOrigin.code === flightDestination?.code && <TextButton
            size="large"
            data-filter="airport"
            kind="tertiary"
            onClick={onToggleFilter}
          >
            Change airport
          </TextButton>}
          {flightsBookable && (!canBuyNow || !!charterBundleRoute) && <OfferBookButton
            size="large"
            type="submit"
            data-testid="HotelAndFlightsBtn"
            disabled={fetching}
            packageView={packageView}
            data-buytype="flights"
            onSubmit={onSubmit}
          >
            {canBuyNow ? 'Book now' : 'Check availability'}
          </OfferBookButton>}
          {flightsBookable && canAddToUserCart && !!charterBundleRoute && <OfferAddToCartButton
            size="large"
            fit="flex"
            type="submit"
            disabled={fetching}
            packageView={packageView}
            data-buytype="flights"
          />}
          {flightsBookable && (canBuyNow && !charterBundleRoute) && <OfferSelectPackageButton
            size="large"
            type="submit"
            disabled={fetching}
            packageView={packageView}
            data-buytype="flights"
            isCharterBundle={false}
          />}
          {flightsBundledNoCache &&
            <Group direction="vertical" gap={12} horizontalAlign="center">
              <BodyText variant="small">OR</BodyText>
              <BodyText variant="medium" weight="bold">
                Call our concierge team for<br />exclusive prices & priority booking
              </BodyText>
              <TextButton
                size="large"
                kind="secondary"
                startIcon={<LinePhoneIcon />}
                href={`tel:${contact.defaultContact.number}`}
                rel="nofollow"
              >
                {contact.defaultContact.humanReadable}
              </TextButton>
            </Group>}
        </Group>}
      >
        <VerticalSpacer gap={12}>
          <div>
            {!hasFlightPrice && flightOrigin.code === flightDestination?.code && <PriceRowCaption>
              No flights available from {flightOrigin.name}. Please select another departing airport.
            </PriceRowCaption>}
            {!hasFlightPrice && !flightsBundledNoCache && flightsBookable && <PriceRowCaption>
              {!isIndiaRegion && 'Add flights to the hotel package. Select from all major airlines at the next step.'}
              {isIndiaRegion && 'Add flights to your hotel package from the best available airlines.'}
            </PriceRowCaption>}
            {!hasFlightPrice && departureTooFarInFuture && <PriceRowCaption>
              Flights can only be booked up to 11 months in advance.
            </PriceRowCaption>}
            {hasFlightPrice && <>
              {checkIn && <PriceRowPriceCaption>
                {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', pkg.duration)} + flights
              </PriceRowPriceCaption>}
              {!checkIn && <PriceRowPriceCaption>
                {showHotelAndFlightsToggle && <LinePlaneDepartureIcon size="S" />} {pluralizeToString('night', pkg.duration)} + flights from
              </PriceRowPriceCaption>}
              <LuxPlusPriceStack
                size="S"
                price={convertToDisplay(pricingWithFlights.price)}
                memberPrice={memberPricingWithFlights?.price && convertToDisplay(memberPricingWithFlights?.price)}
                saleUnit={priceMode === 'perPerson' ? 'person*' : 'total*'}
                testid="offer-package-price-with-flight"
                offerConfig={offer.luxPlus}
              />
              {!rooms && <PriceRowCaption>Two Adults</PriceRowCaption>}
              <PriceRowTaxesAndFees />
              {showIncludedGuestsExceeded && <AdditionalGuestsPopup
                complex={false}
                description="+ Extra guest surcharge"
                modalContent={pkg.roomPolicyDescription}
              />}
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                data-testid="value-box"
                size="M"
                originalValue={convertToDisplay(pricingWithFlights.value)}
              />}
              {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}

              <PriceRowAgentHubCommission
                size="M"
                kind="plain"
                offerId={offer.id}
                productType={offer.productType}
                offerLocations={offerLocations}
                vendorName={vendorName}
                offerTotal={pricingWithFlights.price}
                packageId={pkg.id}
              />
              {showFlightsDealTag && <Label variant="critical" kind="plain" startIcon={<SolidFireIcon />}>Flight deal available</Label>}
              {showPriceDetails && <OfferPriceDetailsRow
                trigger="price-row"
                triggerSize="M"
                offerPage
                offer={offer}
                duration={pkg.duration}
                propertyFees={packageView.propertyFees}
                otherFees={otherFees}
                hotelPrice={packageView.hotelPrice}
                flightPrice={flightPrice}
                rooms={rooms}
                extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
                hotelMemberPrice={packageView.hotelMemberPrice}
                showMemberPrice={showMemberPrice}
                cta={<Group
                  direction="vertical"
                  tabletDirection="horizontal"
                  horizontalAlign="stretch"
                  tabletHorizontalAlign="end"
                  gap={8}
                >
                  {canAddToUserCart && !!charterBundleRoute && <OfferAddToCartButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                    data-buytype="flights"
                  />}
                  {canAddToUserCart && !charterBundleRoute && <OfferSelectPackageButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                    data-buytype="flights"
                    isCharterBundle={false}
                  />}
                  {(!canBuyNow || !!charterBundleRoute) && <OfferBookButton
                    size="large"
                    type="submit"
                    disabled={fetching}
                    packageView={packageView}
                    data-buytype="flights"
                    onSubmit={onSubmit}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </OfferBookButton>}
                </Group>}
              />}
            </>}
            <AtolProtectedTooltip />
          </div>
          {hasFlightPrice && <LoyaltyPointsEarnWithBreakdownModal
            calculationRequests={pointsEarnCalculationRequests}
            calculationType={(!checkIn || (showHotelAndFlightsToggle && isFlightsSelected)) ? 'estimate' : 'exact'}
            modalTitle={packageView.package.roomType.name}
          />}
        </VerticalSpacer>
      </AccommodationPackageTilePricingBlock>}

      {shouldShowCharterFlightErrorMessage && <AccommodationPackageTilePricingBlock
        data-testid="flights-pricing"
        title="Hotel + flights"
      >
        <MessageBanner kind="critical">
          Failed to find charter flight data
        </MessageBanner>
      </AccommodationPackageTilePricingBlock>}
    </AccommodationPackageTilePricingBlocks>
    <LoadingIndicator visible={fetching} floating opaque />
    {showBNBL && <Expander expanded={!selectedTargetUpgradePackage}>
      <OfferPageBNBLcta packageView={packageView} />
    </Expander>}
  </>
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  currency: appState.geo.currentCurrency,
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isLoggedIn: isLoggedInSelector(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  isSpoofed: appState.auth.account.isSpoofed,
  isStoreMode: appState.system.storeMode,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isFlightsSelected: state.flights,
  open: !!state.activeFilter,
  charterBundleRoute: state.charterBundleRoute,
  priceMode: state.priceMode,
}), FlashHotelOptionPriceDetails, preventRenderWithOpenFilter)
